import { useNavigate } from "react-router-dom";

/**
 * Hook returning props to spread onto any row / card element to make it clickable.
 *
 * Usage:
 *   const rowProps = useRowOpen(`/m/${moduleCode}/staff/${row.id}`);
 *   <tr {...rowProps}>...</tr>
 *
 * Interactive children inside the row (buttons, links, inputs, checkboxes) get
 * onClick={(e) => e.stopPropagation()} so they don't trigger the row-open.
 * To prevent accidental triggers, child interactives should have onClick handlers
 * that call e.stopPropagation() themselves — this hook injects an outer onClick
 * that ignores events where target matches "button, a, input, label, select,
 * textarea, [role=button], [data-no-row-click]".
 */
export function useRowOpen(target, options = {}) {
    const navigate = useNavigate();
    const isFunc = typeof target === "function";

    const handle = (e) => {
        // Ignore clicks originating from interactive descendants.
        const t = e.target;
        if (t && t.closest && t.closest("button, a, input, label, select, textarea, [role=menu], [role=dialog], [data-no-row-click]")) {
            return;
        }
        if (isFunc) target(e);
        else if (target) navigate(target);
    };

    const onKey = (e) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            if (isFunc) target(e);
            else if (target) navigate(target);
        }
    };

    return {
        onClick: handle,
        onKeyDown: onKey,
        role: "link",
        tabIndex: 0,
        className: `asl-row-clickable ${options.className || ""}`.trim(),
        "data-testid": options.testid,
    };
}


/**
 * Non-hook variant for use inside `.map()` callbacks where a hook cannot be called.
 * Accepts the `navigate` function from `useNavigate()` plus a target path/function.
 *
 * Example:
 *   const navigate = useNavigate();
 *   items.map((row) => (
 *     <tr key={row.id} {...rowOpenProps(navigate, `/staff/${row.id}`, {testid: `row-${row.id}`})}>
 *       ...
 *     </tr>
 *   ))
 */
export function rowOpenProps(navigate, target, options = {}) {
    const isFunc = typeof target === "function";
    return {
        onClick: (e) => {
            const t = e.target;
            if (t && t.closest && t.closest("button, a, input, label, select, textarea, [role=menu], [role=dialog], [data-no-row-click]")) return;
            if (isFunc) target(e); else if (target) navigate(target);
        },
        onKeyDown: (e) => {
            if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                if (isFunc) target(e); else if (target) navigate(target);
            }
        },
        role: "link",
        tabIndex: 0,
        className: `asl-row-clickable ${options.className || ""}`.trim(),
        "data-testid": options.testid,
    };
}
